// A generative tramline score: a swung walking bass, reed-organ afterbeats, wheels clacking over rail joints,
// a celesta tune and the tram's two-stroke bell. It runs in stretches between stops: at a stop the groove halts,
// a chord holds, the bell rings and it pulls away again, often in a new key. Never the same ride twice.
// Starts on the first click or key press (browsers block audio before that).
export const Music = (() => {
  let ac = null, master = null, verb = null, echo = null, noise = null, timer = null, on = true, vol = 0.5;
  try { const o = JSON.parse(localStorage.getItem('trammed-music') || 'null'); if (o) { on = !!o.on; vol = o.vol ?? vol; } } catch (e) { /* storage blocked */ }
  const save = () => { try { localStorage.setItem('trammed-music', JSON.stringify({ on, vol })); } catch (e) { /* storage blocked */ } };
  const hz = m => 440 * Math.pow(2, (m - 69) / 12);
  const rand = (a, b) => a + Math.random() * (b - a);
  const pick = arr => arr[Math.floor(Math.random() * arr.length)];
  const MODES = { ionian: [0, 2, 4, 5, 7, 9, 11], mixolydian: [0, 2, 4, 5, 7, 9, 10], dorian: [0, 2, 3, 5, 7, 9, 10] };
  // where a chord on each scale degree likes to move next
  const MOVES = { 0: [3, 4, 5, 1, 3], 1: [4, 4, 6], 2: [5, 3], 3: [0, 4, 1, 4], 4: [0, 0, 5, 3], 5: [1, 3, 4], 6: [0, 2] };
  const BEAT = 60 / 96, SWING = 0.62;   // eighths are swung: the offbeat lands late, like a bogie over a joint
  // s.part: 'run' (full groove), 'cruise' (lighter), 'stop' (held chord and the bell)
  const s = { root: 50, mode: 'ionian', chord: 0, beat: 0, next: 0, part: 'cruise', partEnd: 0, bar: 0, mel: 9, phrase: 0, rest: 4 };
  const midi = deg => s.root + 12 * Math.floor(deg / 7) + MODES[s.mode][((deg % 7) + 7) % 7];
  const tones = () => [s.chord, s.chord + 2, s.chord + 4];

  function impulse(sec) {
    const len = Math.floor(ac.sampleRate * sec), buf = ac.createBuffer(2, len, ac.sampleRate);
    for (let c = 0; c < 2; c++) { const d = buf.getChannelData(c); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.2); }
    return buf;
  }
  function init() {
    ac = new (window.AudioContext || window.webkitAudioContext)();
    master = ac.createGain(); master.gain.value = on ? vol : 0;
    const comp = ac.createDynamicsCompressor(); comp.threshold.value = -18; comp.ratio.value = 3;
    master.connect(comp); comp.connect(ac.destination);
    verb = ac.createConvolver(); verb.buffer = impulse(2.2);   // a station hall, not a cathedral
    const verbOut = ac.createGain(); verbOut.gain.value = 0.35; verb.connect(verbOut); verbOut.connect(master);
    echo = ac.createDelay(2); echo.delayTime.value = BEAT * 0.75;
    const fb = ac.createGain(), tone = ac.createBiquadFilter(), echoOut = ac.createGain();
    fb.gain.value = 0.25; tone.type = 'lowpass'; tone.frequency.value = 2200; echoOut.gain.value = 0.25;
    echo.connect(tone); tone.connect(fb); fb.connect(echo); tone.connect(echoOut); echoOut.connect(master);
    noise = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
    const nd = noise.getChannelData(0); for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
    s.root = pick([48, 50, 51, 53, 55]); s.mode = pick(['ionian', 'mixolydian']);
    s.next = ac.currentTime + 0.3; s.beat = 0; s.part = 'stop'; s.partEnd = 8;   // the first thing you hear is the bell
    timer = setInterval(tick, 100);
  }
  function env(t, out, level, attack, decay) {
    const g = ac.createGain(); g.connect(out);
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(level, t + attack); g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    return g;
  }
  function osc(type, f, t, end, out, detune = 0) { const o = ac.createOscillator(); o.type = type; o.frequency.value = f; o.detune.value = detune; o.connect(out); o.start(t); o.stop(end); return o; }

  // a plucked upright bass: round sine body with a short bright attack
  function bass(m, t, level = 0.16) {
    const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.setValueAtTime(900, t); lp.frequency.exponentialRampToValueAtTime(220, t + 0.25); lp.connect(master);
    const g = env(t, lp, level, 0.008, 0.55);
    osc('triangle', hz(m), t, t + 0.7, g); osc('sine', hz(m), t, t + 0.7, g);
  }
  // a reed organ chord, short on the afterbeat or held at a stop
  function reed(ms, t, dur, level) {
    const lp = ac.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 1500; lp.Q.value = 0.7; lp.connect(master);
    const send = ac.createGain(); send.gain.value = 0.5; lp.connect(send); send.connect(verb);
    const g = ac.createGain(); g.connect(lp);
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(level, t + 0.02);
    g.gain.setTargetAtTime(level * 0.7, t + 0.03, 0.08); g.gain.setTargetAtTime(0, t + dur, 0.06);
    const lfo = ac.createOscillator(), depth = ac.createGain(); lfo.frequency.value = 5.2; depth.gain.value = 6; lfo.connect(depth);
    lfo.start(t); lfo.stop(t + dur + 0.5);
    for (const m of ms) for (const [type, cents] of [['sawtooth', -4], ['square', 5]]) {
      const o = osc(type, hz(m), t, t + dur + 0.5, g, cents); depth.connect(o.detune);
    }
  }
  // a celesta note for the tune
  function celesta(m, t, level) {
    const g = env(t, master, level, 0.004, rand(0.9, 1.3));
    const send = ac.createGain(); send.gain.value = 0.4; g.connect(send); send.connect(echo); g.connect(verb);
    for (const [mul, amp] of [[1, 1], [4.0, 0.12], [2.0, 0.18]]) { const a = ac.createGain(); a.gain.value = amp; a.connect(g); osc('sine', hz(m) * mul, t, t + 1.5, a); }
  }
  // wheels over a rail joint: a dull knock with a metallic tick on top
  function clack(t, level) {
    const src = ac.createBufferSource(); src.buffer = noise;
    const bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = rand(1800, 2600); bp.Q.value = 2.5;
    const g = env(t, master, level, 0.002, 0.045); src.connect(bp); bp.connect(g);
    src.start(t, Math.random() * 0.5, 0.08);
    const k = env(t, master, level * 1.6, 0.002, 0.07); osc('sine', rand(85, 100), t, t + 0.1, k);
  }
  // brushes on a snare, soft enough to sit under everything
  function brush(t, level, len) {
    const src = ac.createBufferSource(); src.buffer = noise;
    const hp = ac.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 3500;
    const g = env(t, master, level, len * 0.4, len); src.connect(hp); hp.connect(g);
    src.start(t, Math.random() * 0.5, len + 0.2);
  }
  // the tram bell: inharmonic partials, struck
  function bell(m, t, level) {
    const g = env(t, master, level, 0.002, 1.8); g.connect(verb);
    for (const [mul, amp] of [[1, 1], [2.76, 0.45], [5.4, 0.22], [8.93, 0.08]]) { const a = ac.createGain(); a.gain.value = amp; a.connect(g); osc('sine', hz(m) * mul, t, t + 2, a); }
  }
  const dingDing = (t, level = 0.07) => { const m = s.root + 31; bell(m, t, level); bell(m, t + 0.2, level * 0.85); };

  function newPart() {
    if (s.part !== 'stop' && Math.random() < 0.4) {
      // pull into a stop: hold the home chord, ring the bell, and maybe set off in a new key
      s.part = 'stop'; s.partEnd = s.beat + 8; s.chord = 0;
      return;
    }
    if (s.part === 'stop' && Math.random() < 0.6) {
      let r = s.root + pick([5, -7, 2, -2, 7, -5, 3, -3]);
      while (r > 55) r -= 12; while (r < 45) r += 12;
      s.root = r; s.mode = pick(['ionian', 'ionian', 'mixolydian', 'dorian']);
    }
    s.part = Math.random() < 0.6 ? 'run' : 'cruise';
    s.partEnd = s.beat + 4 * pick([16, 16, 24, 32]);
  }
  function tick() {
    if (!on) return;
    while (s.next < ac.currentTime + 0.5) {
      const t = s.next + rand(-0.006, 0.006), b = s.beat % 4, run = s.part === 'run';
      if (s.beat >= s.partEnd) newPart();
      if (s.part === 'stop') {
        if (s.beat + 8 === s.partEnd) { dingDing(t); reed([midi(7), midi(9), midi(11)], t, BEAT * 6.5, 0.022); bass(midi(0) - 12, t, 0.12); }
        if (s.beat + 2 === s.partEnd) dingDing(t, 0.05);   // ding ding: doors closed, away we go
      } else {
        if (b === 0) { if (s.bar % 2 === 0) s.chord = pick(MOVES[s.chord]); s.bar++; }
        const ct = tones(), root = midi(ct[0]) - 12, fifth = midi(ct[2]) - 12;
        // bass: two-feel in a cruise, walking quarters on a run, leading into the next chord
        if (run || b === 0 || b === 2) bass(b === 0 ? root : b === 2 ? fifth : b === 1 ? midi(ct[1]) - 12 : root + pick([-1, 1, 2]), t);
        // reed afterbeats on 2 and 4
        if (b === 1 || b === 3) reed([midi(ct[0] + 7), midi(ct[1] + 7), midi(ct[2] + 7)], t, BEAT * 0.35, run ? 0.02 : 0.014);
        // rail joints: knock-knock on 1 and 3, the second wheel landing on the swung offbeat
        if (b === 0 || b === 2) { clack(t, 0.03); clack(t + BEAT * SWING, 0.022); }
        if (run) { brush(t, 0.012, 0.09); brush(t + BEAT * SWING, 0.008, 0.06); }
        // tune: short swung celesta phrases that land on chord tones
        for (const off of [0, SWING]) {
          if (s.rest > 0) { if (off === 0) s.rest--; continue; }
          if (s.phrase <= 0) s.phrase = Math.floor(rand(4, 9));
          if (Math.random() < (off === 0 ? 0.7 : 0.4) * (run ? 1 : 0.6)) {
            s.mel += pick([-2, -1, -1, 1, 1, 2, 0, 2, -3]);
            if (s.mel < 7) s.mel += 2; if (s.mel > 16) s.mel -= 3;
            if (off === 0 && b % 2 === 0) s.mel = ct.map(d => d + 7 * Math.round((s.mel - d) / 7)).reduce((a, c) => Math.abs(c - s.mel) < Math.abs(a - s.mel) ? c : a);
            celesta(midi(s.mel) + 12, t + off * BEAT, rand(0.04, 0.06));
            if (--s.phrase <= 0) s.rest = Math.floor(rand(3, 10));
          }
        }
      }
      s.beat++; s.next += BEAT;
    }
  }
  function apply() {
    if (!ac) return;
    master.gain.cancelScheduledValues(ac.currentTime);
    master.gain.setTargetAtTime(on ? vol : 0, ac.currentTime, 0.4);
    if (on) s.next = Math.max(s.next, ac.currentTime + 0.1);
  }
  return {
    start() { if (!ac) init(); if (ac.state === 'suspended') ac.resume(); },
    toggle() { on = !on; save(); this.start(); apply(); },
    // a victory moment: the bell, then a bright celesta arpeggio
    chime() { if (!ac || !on) return; const t = ac.currentTime + 0.02; dingDing(t, 0.09); [0, 4, 7, 12].forEach((d, i) => celesta(s.root + 24 + d, t + 0.45 + i * 0.09, 0.08)); },
    setVolume(v) { vol = v; save(); apply(); },
    get on() { return on; }, get volume() { return vol; }, get ctx() { return ac; }, get out() { return master; },
  };
})();
